import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { DialogOpenEvent } from '@theme/dialog';
import { fetchCartState, companionOverageUpdates } from '@theme/cart-state';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * Remembers the plan a companion subscription line had when its toggle was
 * unchecked, keyed by the unit's variant id, so re-checking restores the
 * customer's chosen frequency instead of the default. Module-level so it
 * survives section morphs.
 * @type {Map<string, {variantId: string, sellingPlan: string}>}
 */
const companionPlanMemory = new Map();

/**
 * Resolves a unit and its companion subscription lines BY ROLE, mirroring
 * cart-products.liquid's ownership rule: unit = the OLDEST line of the unit
 * variant (last match in newest-first cart.items), companions = lines with a
 * selling plan whose `_filter_for_variant` is that variant. Keys are taken
 * from `items`, so they are only as fresh as the cart they came from.
 * @param {any[]} items - Newest-first cart.items.
 * @param {string} unitVariant - The unit's variant id.
 * @returns {{unit: {key: string, quantity: number}, updatesAt: (quantity: number) => Record<string, number>} | null}
 *   null when the unit is no longer in the cart.
 */
function companionRolesIn(items, unitVariant) {
  let unitLine = null;
  for (const item of items) {
    if (String(item.variant_id) === unitVariant) unitLine = item;
  }
  if (!unitLine) return null;

  const companionKeys = items
    .filter((item) => item.selling_plan_allocation && item.properties?.['_filter_for_variant'] === unitVariant)
    .map((item) => item.key);
  const unit = { key: unitLine.key, quantity: unitLine.quantity };

  return {
    unit,
    updatesAt(quantity) {
      /** @type {Record<string, number>} */
      const updates = { [unit.key]: quantity };
      for (const key of companionKeys) updates[key] = quantity;
      return updates;
    },
  };
}

/**
 * Every connected cart-items-component. Today exactly one renders per page
 * (the drawer section is guarded by `template.name != 'cart'`, so the drawer
 * and the cart page never coexist), but the checkout guard waits on the whole
 * set rather than on `this` alone: if a second instance is ever rendered, a
 * mutation owned by the other one must still hold the checkout submit.
 * @type {Set<any>}
 */
const cartItemsInstances = new Set();

/**
 * Abort ceiling for cart mutation requests.
 *
 * Load-bearing for CHECKOUT, not just for the drawer. `#cartLock` is released
 * only in a request chain's `finally`, and `onCartFormSubmit` awaits that lock —
 * so a request that never settles (flaky mobile, a dropped connection) meant the
 * lock never released, the guard's await never returned, and the customer could
 * not check out at all: the button stuck `aria-busy` inside a
 * `pointer-events: none` drawer, with only Escape as a way out. Bounding the
 * request turns a hang into an ordinary failure, which every caller already
 * self-heals from by re-rendering server truth.
 */
const CART_REQUEST_TIMEOUT_MS = 15000;

/**
 * Ceiling on how long the checkout submit is held while cart work settles.
 * Comfortably above one bounded request plus a debounce window, so it only
 * trips on a pathological re-arm loop — never on ordinary stepper traffic.
 */
const CHECKOUT_GUARD_MAX_WAIT_MS = 20000;

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  /**
   * One debounced commit PER CART LINE.
   *
   * A single shared debouncer coalesced by TIME, not by line: `debounce`
   * keeps only the LAST call's arguments, so pressing + on one row and then
   * another inside the 300ms window silently dropped the first row's change.
   * The stepper had already painted the new number locally, so the shopper
   * saw 2 / 2 while the server still held 1 — and checkout (correctly) billed
   * server truth. Reproduced 2026-08-18: two + clicks 120ms apart emitted a
   * single `/cart/change {line:1,quantity:2}` and the other line never moved.
   *
   * Keyed by cart line, each row waits out its own window. Entries for lines
   * that later vanish sit idle (never pending) and are harmless.
   *
   * Bind the inner method, not the debounced wrapper: .bind() returns a new
   * function without the .cancel/.pending/.flush properties the checkout
   * guard relies on.
   * @type {Map<number, ReturnType<typeof debounce>>}
   */
  #lineDebouncers = new Map();

  /**
   * Routes a quantity-selector event to its own line's debouncer.
   * @param {QuantitySelectorUpdateEvent} event
   */
  #onQuantitySelectorUpdate = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const line = event.detail?.cartLine;
    // Cart items require a line number (PDP/quick-order selectors have none).
    if (!line) return;

    // Capture the row's line-item KEY now. `cartLine` is POSITIONAL against
    // newest-first cart.items, and anything that adds or removes a line inside
    // the 300ms window shifts every line after it — see #onQuantityChange.
    const key = this.#rowForLine(line)?.dataset.key;

    let debounced = this.#lineDebouncers.get(line);
    if (!debounced) {
      debounced = debounce(this.#onQuantityChange.bind(this), 300);
      this.#lineDebouncers.set(line, debounced);
    }
    debounced(event, key);
  };

  /** @returns {boolean} Whether any line has a change waiting out its window. */
  #hasPendingLineChange() {
    for (const debounced of this.#lineDebouncers.values()) {
      if (debounced.pending()) return true;
    }
    return false;
  }

  /** Commits every waiting line change immediately. */
  #flushLineChanges() {
    for (const debounced of this.#lineDebouncers.values()) debounced.flush();
  }

  /**
   * Resolves when every in-flight mutation has released the cart lock.
   * Non-null exactly while `cart-items-disabled` is set. Acquisition is
   * counted (#cartLockDepth): nested/overlapping mutations share one lock
   * and only the last release resolves it.
   * @type {{promise: Promise<void>, release: () => void} | null}
   */
  #cartLock = null;

  /** @type {number} */
  #cartLockDepth = 0;

  /** True while onCartFormSubmit re-issues the deferred native submit. */
  #resubmittingCartForm = false;

  /**
   * `fetch` with an abort timeout (see CART_REQUEST_TIMEOUT_MS). An abort
   * surfaces as a rejection, so it lands in the caller's existing catch.
   * @param {string} url
   * @param {RequestInit} init
   * @returns {Promise<Response>}
   */
  #fetchCart(url, init) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), CART_REQUEST_TIMEOUT_MS);
    return fetch(url, { ...init, signal: controller.signal }).finally(() => clearTimeout(timeout));
  }

  /** @type {Element | null} */
  #drawer = null;

  connectedCallback() {
    super.connectedCallback();

    cartItemsInstances.add(this);

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantitySelectorUpdate);
    window.addEventListener('pageshow', this.#handlePageShow);

    // Freshness check when the drawer opens — see #handleDrawerOpen.
    this.#drawer = this.closest('cart-drawer-component');
    this.#drawer?.addEventListener(DialogOpenEvent.eventName, this.#handleDrawerOpen);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    cartItemsInstances.delete(this);

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantitySelectorUpdate);
    window.removeEventListener('pageshow', this.#handlePageShow);

    this.#drawer?.removeEventListener(DialogOpenEvent.eventName, this.#handleDrawerOpen);
    this.#drawer = null;
  }

  /**
   * Re-renders the cart from the server when the page is restored from the
   * back/forward cache (e.g. tapping Back from checkout): the restored DOM is
   * a frozen snapshot and the cart may have changed since — same pattern as
   * cart-icon.js's bubble refresh.
   * @param {PageTransitionEvent} event
   */
  #handlePageShow = (event) => {
    if (!event.persisted) return;
    this.#reRender();
  };

  /**
   * Re-renders the drawer when its server-rendered contents disagree with the
   * live cart.
   *
   * WHY THIS EXISTS (QA 2026-08-18: "coming back from checkout shows an empty
   * cart"). The drawer's body is server-rendered into every page. On `main` the
   * drawer lived INSIDE the header section, and `sections/header.liquid` calls
   * `hydrate(section.id, url)` on every page load — so the drawer body was
   * refreshed from the server on every navigation, for free. Moving the drawer
   * out to its own section (required, for IOM's header-group price exclusion)
   * silently dropped that refresh: nothing calls `hydrate('cart-drawer')`.
   *
   * So a page served from Shopify's full-page cache — rendered when the cart was
   * empty — shows an EMPTY drawer while the cart has items. The header bubble
   * self-corrects (cart-icon.js syncs it from /cart.js), which is exactly why the
   * symptom is "badge says 2, drawer says empty".
   *
   * Checking on OPEN rather than on load is deliberate: zero extra requests per
   * page view, and the check happens at the only moment the contents matter. The
   * `/cart.js` read is the shared deduped one (`@theme/cart-state`), and the
   * section re-render only fires on an actual mismatch, so a correct drawer
   * costs nothing and never flickers.
   */
  #handleDrawerOpen = () => {
    fetchCartState()
      .then((cart) => {
        if (!cart || typeof cart.item_count !== 'number') return;
        const rendered = this.refs.cartItemCount?.textContent;
        // No count node rendered at all means the empty-cart template is showing.
        const renderedCount = rendered == null ? 0 : parseInt(rendered, 10);
        if (Number.isFinite(renderedCount) && renderedCount === cart.item_count) return;
        return this.#reRender();
      })
      .catch((error) => console.error('[cart] drawer freshness check failed', error));
  };

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   * @param {string} [armedKey] - The row's line-item key when the change was
   *   armed, used to re-resolve its (positional) line number at commit time.
   * @param {boolean} [deferred] - True on a re-armed pass (see the in-flight
   *   guard below). The morph that ran in the meantime can REPLACE the stepper's
   *   input node, so `event.target` is detached by then and the containment
   *   check would silently drop the change — the very failure the per-line
   *   debouncers were introduced to fix. On a deferred pass the armed key's row
   *   is the source of truth instead, and it is validated below.
   */
  #onQuantityChange(event, armedKey, deferred = false) {
    const targetConnected = event.target instanceof Node && this.contains(event.target);
    if (!targetConnected && !(deferred && armedKey)) return;

    const { quantity } = event.detail;
    let line = event.detail.cartLine;

    // Cart items require a line number
    if (!line) return;

    // Never commit a positional line number while another mutation is in flight.
    // The DOM still shows PRE-morph line numbers at that moment, so re-resolving
    // from it reads the same stale value — this is a server-ordering race, not a
    // DOM-staleness one. Captured sequence before this guard: Remove on the
    // newest row posts {line:1,quantity:0}; 300ms later this handler posts
    // {line:2,quantity:2}; the removal lands first and shifts every line, so
    // line 2 now addresses what was line 3 and the customer's step is applied to
    // a product they never touched (verified: stepped product stayed at 1, the
    // untouched one went to 2).
    //
    // Re-arm instead of dropping: once the in-flight mutation settles and morphs
    // the section, data-line is server-fresh and the re-resolution below is
    // correct. The checkout guard's flush loop cooperates — a flush that lands
    // here re-arms, the guard awaits the lock, then flushes again and commits;
    // its own deadline bounds any pathological case.
    if (this.#cartLock) {
      const pending = this.#lineDebouncers.get(event.detail.cartLine);
      if (pending) {
        pending(event, armedKey, true);
        return;
      }
    }

    // Re-resolve the line from the row's own key. The captured `cartLine` is
    // POSITIONAL against newest-first cart.items and may be up to 300ms old;
    // after the morph above, data-line is server-fresh. A key with no row left
    // means the line is gone — drop rather than guess, exactly as a
    // flushed-but-morphed-away change is dropped.
    if (armedKey) {
      const armedRow = this.refs.cartItemRows.find((row) => row.dataset.key === armedKey);
      if (!armedRow) return;
      const liveLine = Number(armedRow.dataset.line);
      if (Number.isFinite(liveLine) && liveLine > 0) line = liveLine;
    }

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    const lineItemRow = this.#rowForLine(line);

    // Companion filter-subscription quantities MIRROR their unit's quantity:
    // when the changed line owns companion rows, move all of them in one
    // atomic /cart/update.js call (one request, one morph — same mechanism
    // as the removal cascade). Duplicate unit lines (_upsell) own no
    // companions and keep the plain single-line path.
    const unitKey = lineItemRow?.dataset.key;
    const companionRows = unitKey ? this.#companionRowsFor(unitKey) : [];

    if (unitKey && companionRows.length > 0) {
      this.#updateUnitWithCompanions(unitKey, quantity, { line, action: 'change' });
    } else {
      this.updateQuantity({ line, quantity, action: 'change' });
    }

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  /**
   * Re-renders this section from server truth, swallowing failures.
   *
   * Every self-heal path funnels through here. `renderSection` is async and
   * rejects on a failed fetch; `morphSection` throws by design when the section
   * id is missing from the response. Called bare (un-awaited, un-caught) those
   * rejections were unhandled — and worse, when offline the self-heal itself
   * fails, so the drawer silently kept optimistic values with no feedback. This
   * cannot itself throw, so it is safe to call from a catch or a finally.
   * @returns {Promise<void>}
   */
  async #reRender() {
    try {
      await sectionRenderer.renderSection(this.sectionId, { cache: false });
    } catch (error) {
      console.error('[cart] could not re-render from server truth', error);
    }
  }

  /**
   * Finds a cart item row by its cart line number. Rows are rendered
   * oldest-first (display order ≠ cart order), so positional indexing into
   * cartItemRows would grab the wrong row — each row carries data-line.
   * @param {number} line - The 1-based cart line number.
   * @returns {HTMLTableRowElement | undefined}
   */
  #rowForLine(line) {
    return this.refs.cartItemRows.find((row) => row.dataset.line === String(line));
  }

  /**
   * Companion filter-subscription rows attached to a unit line's key — the
   * same join the removal cascade and the quantity mirror both rely on.
   * @param {string} key - The unit row's line item key.
   * @returns {HTMLElement[]}
   */
  #companionRowsFor(key) {
    return this.refs.cartItemRows.filter((row) => row.dataset.companionParentKey === key);
  }

  /**
   * Applies one quantity to a unit line and its companion subscription lines
   * in a single atomic /cart/update.js call, re-resolving the line keys from
   * a fresh /cart.js first.
   *
   * The DOM's data-key values are only as fresh as the last theme morph, and
   * the IOM engine re-stamps `_iom_line` properties out-of-band — every stamp
   * changes the line's key. /cart/update.js does NOT reject a stale key:
   * Shopify falls back to the key's variant-id prefix and applies the
   * quantity to the NEWEST line of that variant. The filter variant usually
   * also sits in the cart as a one-time `_upsell` line (newer than the
   * subscription), so a stale companion key silently moved the UPSELL line's
   * quantity instead of the subscription's (verified 2026-08-13).
   *
   * Fresh keys are re-derived by role, mirroring cart-products.liquid's
   * ownership rule: unit = the OLDEST line of the unit's variant (last match
   * in newest-first cart.items), companions = lines with a selling plan whose
   * `_filter_for_variant` is that variant. If /cart.js is unreachable the
   * DOM keys go out as before (no worse than the pre-fix behavior); if the
   * unit has left the cart entirely, re-render server truth instead.
   *
   * INCREASES go through /cart/change.js on the unit first: /cart/update.js
   * does not enforce the unit's availability cap at all (verified 2026-10-07:
   * it took an AD4000 capped at 3 to 5), so the atomic write put 4 units + 4
   * subscriptions in the cart and checkout then cut only the unit back to 3.
   * change.js enforces the cap (422, keeping the allowed amount); the
   * subscriptions then follow whatever the unit actually landed at.
   *
   * @param {string} unitKey - The unit row's (possibly stale) line key.
   * @param {number} quantity - The quantity for the unit + companion lines.
   * @param {{line: number, action: string}} config - Passed to updateQuantity.
   */
  async #updateUnitWithCompanions(unitKey, quantity, { line, action }) {
    // Lock the cart NOW, not inside updateQuantity: the key refetch below is
    // a full network round trip, and a second stepper click or Remove firing
    // during it would race this update (and can resurrect lines or move the
    // wrong quantities via the stale-key fallback). The lock is counted, so
    // this hold pairs with the finally below while updateQuantity manages
    // its own nested hold.
    this.#disableCartItems();

    try {
      const unitVariant = unitKey.split(':')[0];

      /** @type {Record<string, number>} */
      let updates = { [unitKey]: quantity };
      for (const row of this.#companionRowsFor(unitKey)) {
        if (row.dataset.key) updates[row.dataset.key] = quantity;
      }

      // The bail-out below must NOT be swallowed by this catch. The catch exists
      // only for a fetchCartState failure (fall through with DOM-derived keys);
      // `renderSection` also rejects on a failed fetch or a morphSection throw,
      // and when that rejection was caught here execution fell through and
      // POSTed the STALE DOM keys for a unit no longer in the cart — Shopify
      // then resolves a stale key by variant-id prefix onto the newest line of
      // that variant, which is exactly the `_upsell`-line corruption this helper
      // exists to prevent (companion-filter-subscription.md §4).
      let unitMissing = false;
      /** @type {{key: string, quantity: number} | null} */
      let freshUnit = null;
      try {
        // fresh: joining an in-flight /cart.js started before an IOM stamp
        // would hand back the very stale keys this refetch exists to replace.
        const cart = await fetchCartState({ fresh: true });
        if (Array.isArray(cart?.items)) {
          const roles = companionRolesIn(cart.items, unitVariant);
          if (!roles) {
            unitMissing = true;
          } else {
            freshUnit = roles.unit;
            updates = roles.updatesAt(quantity);
          }
        }
      } catch (error) {
        // The fresh read is what makes this write safe. Falling through to the
        // DOM-derived keys used to feel like the resilient choice; it is the
        // opposite. IOM churns line keys out of band, `/cart/update.js` does NOT
        // reject a stale key, and Shopify resolves one by its variant-id PREFIX
        // onto the NEWEST line of that variant — so an unverified write lands on
        // the wrong line whenever the filter variant is also in the cart as a
        // one-time `_upsell` (companion-filter-subscription.md §4).
        //
        // Throttling makes this concrete rather than theoretical: a 429 on the
        // read is exactly when keys are least verifiable, and the engine drives
        // the store into 429 routinely. So abort and re-render to server truth.
        // The shopper loses this one change; that is strictly better than moving
        // a quantity onto a product they did not touch, and it matches how the
        // theme already drops a flushed change whose row was morphed away.
        console.error('[cart] could not verify cart lines before a multi-line update; aborting', error);
        await this.#reRender();
        return;
      }

      if (unitMissing) {
        // Re-render instead of writing: the unit is gone server-side.
        await this.#reRender();
        return;
      }

      let capMessage = '';
      if (freshUnit && quantity > freshUnit.quantity) {
        const landed = await this.#increaseUnitWithinCap(freshUnit.key, quantity);
        // Re-resolve every key from the cart AFTER the change: IOM's fetch
        // wrapper re-stamps `_iom_line` onto /cart/change.js writes
        // (elitecart-iom-zero-price-fix.md §8), which re-keys the unit, so the
        // pre-change keys are stale by construction here — and a stale key in
        // the /cart/update.js below lands on the newest line of the variant
        // (an `_upsell` duplicate) instead of the unit. Match by role, never
        // by the old key.
        const roles = landed && companionRolesIn(landed.items, unitVariant);
        if (!roles) {
          await this.#reRender();
          return;
        }
        freshUnit = roles.unit;
        const target = Math.min(quantity, roles.unit.quantity);
        if (target < quantity) capMessage = landed.message;
        updates = roles.updatesAt(target);
      }

      await this.updateQuantity({ line, quantity, action, updates });

      // The morph above reset the stepper to the capped quantity; say why.
      if (capMessage && freshUnit) {
        const unitKey = freshUnit.key;
        const unitRow = this.refs.cartItemRows.find((row) => row.dataset.key === unitKey);
        if (unitRow) this.#handleCartError(Number(unitRow.dataset.line), { errors: capMessage });
      }
    } finally {
      this.#enableCartItems();
    }
  }

  /**
   * Raises a unit line through /cart/change.js, which (unlike
   * /cart/update.js) enforces the variant's availability cap: past the cap it
   * answers 422 but keeps the allowed amount. Returns the cart as it stands
   * after the change — the caller must re-resolve keys from it.
   * @param {string} key - The unit line's fresh key.
   * @param {number} quantity - The requested quantity.
   * @returns {Promise<{items: any[], message: string} | null>} null when the
   *   outcome can't be determined (the caller re-renders server truth).
   */
  async #increaseUnitWithinCap(key, quantity) {
    try {
      const response = await this.#fetchCart(
        Theme.routes.cart_change_url,
        fetchConfig('json', { body: JSON.stringify({ id: key, quantity }) })
      );
      const result = await response.json();
      // A capped change answers 422 as {"errors": "Only 3 items were added…"}
      // (no `status`, no cart) — or as a {status, description} payload — and
      // keeps the allowed amount either way; read where the unit landed.
      const capped = !Array.isArray(result.items);
      const message = capped
        ? String((typeof result.errors === 'string' && result.errors) || result.description || result.message || '')
        : '';
      const cart = capped ? await fetchCartState({ fresh: true }) : result;
      return Array.isArray(cart?.items) ? { items: cart.items, message } : null;
    } catch (error) {
      console.error('[cart] could not raise the unit quantity', error);
      return null;
    }
  }

  /**
   * Toggles a companion filter subscription from its cart checkbox card.
   * Unchecking removes the subscription line (the morph re-renders the card
   * as an unchecked offer under the unit); checking adds it back with the
   * remembered plan, or the card's default.
   *
   * Wired twice: on:click on the card header (mouse — the hidden input is
   * deliberately NOT label-associated, since label focus-forwarding scrolls
   * the drawer mid-click and the retargeted click never activates it) and
   * on:change on the input (keyboard). The click branch just forwards to the
   * input, whose change event re-enters this handler.
   * @param {Event} event
   */
  onCompanionToggle(event) {
    if (event.type === 'click') {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const row = target.closest('.cart-items__table-row--companion');
      const checkbox = row?.querySelector('.cart-items__companion-checkbox');
      if (checkbox instanceof HTMLInputElement && !checkbox.disabled) checkbox.click();
      return;
    }

    const checkbox = event.target;
    if (!(checkbox instanceof HTMLInputElement)) return;

    // The morph renders the fresh state; block double toggles meanwhile.
    checkbox.disabled = true;

    const parentVariant = checkbox.dataset.companionParentVariant ?? '';

    if (checkbox.checked) {
      const remembered = companionPlanMemory.get(parentVariant);
      const variantId = remembered?.variantId ?? checkbox.dataset.companionVariantId;
      const sellingPlan = remembered?.sellingPlan ?? checkbox.dataset.companionSellingPlan;
      if (!variantId || !sellingPlan) {
        checkbox.disabled = false;
        return;
      }

      /** @type {Record<string, string>} */
      const properties = { _filter_for_variant: parentVariant };
      if (checkbox.dataset.companionProductId) properties['_product_id'] = checkbox.dataset.companionProductId;
      if (checkbox.dataset.companionVariantSku) properties['_variant_sku'] = checkbox.dataset.companionVariantSku;

      // Quantity mirrors the unit line, read fresh from the server-rendered
      // offer row (morph-maintained) — never memorized alongside the plan.
      const quantity = Math.max(1, Number(checkbox.dataset.companionUnitQuantity) || 1);

      this.#addCompanionLine({ variantId, sellingPlan, properties, quantity });
    } else {
      if (parentVariant && checkbox.dataset.companionVariantId && checkbox.dataset.companionSellingPlan) {
        companionPlanMemory.set(parentVariant, {
          variantId: checkbox.dataset.companionVariantId,
          sellingPlan: checkbox.dataset.companionSellingPlan,
        });
      }

      const line = Number(checkbox.dataset.companionLine);
      if (line) {
        this.updateQuantity({
          line,
          quantity: 0,
          action: 'clear',
        });
      }
    }
  }

  /**
   * Adds a companion filter-subscription line and morphs the cart from the
   * response sections (mirrors updateQuantity's response handling).
   * @param {{variantId: string, sellingPlan: string, properties: Record<string, string>, quantity?: number}} config
   */
  async #addCompanionLine({ variantId, sellingPlan, properties, quantity = 1 }) {
    // Lock the cart like every other mutation path: only the offer checkbox
    // is disabled otherwise, so a Remove or stepper click during the add
    // could race it (orphaned subscription / broken quantity mirror).
    this.#disableCartItems();

    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    cartTotal?.shimmer();

    const body = JSON.stringify({
      items: [
        {
          id: Number(variantId),
          quantity,
          selling_plan: Number(sellingPlan),
          properties,
        },
      ],
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    try {
      const response = await this.#fetchCart(`${Theme.routes.cart_add_url}`, fetchConfig('json', { body }));
      const data = await response.json();

      resetShimmer(this);

      if (data.status) {
        console.error(data.description || data.message);
        this.#reRender();
        return;
      }

      const newSectionHTML = new DOMParser().parseFromString(data.sections[this.sectionId], 'text/html');
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      this.dispatchEvent(
        new CartAddEvent(undefined, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: data.sections,
        })
      );

      morphSection(this.sectionId, data.sections[this.sectionId], { mode: this.isDrawer ? 'hydration' : 'full' });
    } catch (error) {
      console.error(error);
      this.#reRender();
    } finally {
      this.#enableCartItems();
    }
  }

  /**
   * Guards the checkout submit against in-flight cart mutations.
   *
   * The cart form's native POST carries no line data (the quantity inputs
   * are unnamed — quantity-selector.liquid), so submitting can never corrupt
   * the cart. What it can still do is navigate away while a quantity change
   * sits in the 300ms debounce window or its request is mid-flight — the
   * customer would see pre-change quantities at checkout. In that case:
   * cancel this submission, flush the debounced change, wait for the cart
   * lock to clear, then RE-ISSUE the native submit — the deferred POST still
   * carries the note textarea and the submitter's formaction (attribution.js
   * stamps its UTM params there on click), unlike a location.assign. The
   * idle path returns untouched so the normal native POST proceeds.
   * @param {SubmitEvent} event
   */
  async onCartFormSubmit(event) {
    // Our own re-issued submit passing through.
    if (this.#resubmittingCartForm) return;
    if (!CartItemsComponent.#anyWorkPending()) return;

    event.preventDefault();

    const submitter = event.submitter instanceof HTMLElement ? event.submitter : null;

    // Flush-and-wait until nothing is pending and nothing is in flight.
    // A flushed event whose row was morphed away no-ops inside
    // #onQuantityChange (deliberately: re-applying by a line number that may
    // have shifted server-side could hit the wrong line) — the customer then
    // checks out with server-truth quantities. New keyboard-driven changes
    // arriving mid-wait are picked up by the next iteration. Best-effort:
    // any throw here must not strand the click with its default prevented.
    this.#setCheckoutBusy(true);

    try {
      // Bounded: never trap the customer on this screen. Each iteration is at
      // most one request (now itself abort-bounded), but a change that re-arms
      // as fast as it is flushed — a blur handler firing on a morphed-in input,
      // say — would otherwise spin forever with the click held. On expiry we
      // proceed to checkout with server-truth quantities, which is the same
      // outcome as a flushed change whose row was morphed away.
      const deadline = Date.now() + CHECKOUT_GUARD_MAX_WAIT_MS;
      while (CartItemsComponent.#anyWorkPending()) {
        if (Date.now() > deadline) {
          console.error('[cart] checkout guard timed out waiting for pending cart work; proceeding');
          break;
        }
        for (const instance of cartItemsInstances) instance.#flushLineChanges();
        await Promise.all(Array.from(cartItemsInstances, (instance) => instance.#cartLock?.promise));
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.#setCheckoutBusy(CartItemsComponent.#anyWorkPending());
    }

    // Re-issue on the form that was actually submitted, falling back to this
    // component's form only if the settle morph detached the original.
    // (Only one cart-items-component renders per page — the drawer section is
    // guarded by `template.name != 'cart'` — but resolving from the event is
    // still the honest lookup: it can't drift if that ever changes.)
    const submittedForm = event.target instanceof HTMLFormElement ? event.target : null;
    const form = submittedForm?.isConnected ? submittedForm : this.querySelector('form.cart-form');
    // No form left (a flush emptied the cart) means nothing to check out.
    if (!(form instanceof HTMLFormElement)) return;

    // The checkout button lives OUTSIDE the form (associated by form="cart-form"),
    // so ownership is `.form`, never DOM containment — and requestSubmit()
    // rejects a submitter the form does not own.
    let freshSubmitter = submitter?.isConnected ? submitter : this.#checkoutButton();
    if (!(freshSubmitter instanceof HTMLButtonElement) || freshSubmitter.form !== form) freshSubmitter = null;
    // Carry the attribution-stamped formaction across a morph-replaced button.
    if (freshSubmitter && submitter && !freshSubmitter.getAttribute('formaction')) {
      const formaction = submitter.getAttribute('formaction');
      if (formaction) freshSubmitter.setAttribute('formaction', formaction);
    }

    this.#resubmittingCartForm = true;
    try {
      if (freshSubmitter instanceof HTMLButtonElement) {
        form.requestSubmit(freshSubmitter);
      } else {
        form.requestSubmit();
      }
    } catch (error) {
      console.error(error);
      window.location.assign(freshSubmitter?.getAttribute('formaction') || '/checkout');
    } finally {
      this.#resubmittingCartForm = false;
    }
  }

  onLineItemRemove(line) {
    const cartItemRowToRemove = this.#rowForLine(line);

    // Companion filter-subscription lines (data-companion-parent-key) are a
    // combo with their unit: removing the unit removes the subscription too,
    // in one atomic /cart/update.js call.
    const removedKey = cartItemRowToRemove?.dataset.key;
    const companionRows = removedKey ? this.#companionRowsFor(removedKey) : [];

    if (removedKey && companionRows.length > 0) {
      this.#updateUnitWithCompanions(removedKey, 0, { line, action: 'clear' });
    } else {
      this.updateQuantity({
        line,
        quantity: 0,
        action: 'clear',
      });
    }

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove, plus companion
      // filter-subscription lines removed in the same call
      ...this.refs.cartItemRows.filter(
        (row) =>
          row.dataset.parentKey === cartItemRowToRemove.dataset.key ||
          (removedKey && row.dataset.companionParentKey === removedKey)
      ),
      // Unchecked companion offer cards attached to the removed unit
      ...(removedKey ? this.querySelectorAll(`tr[data-companion-offer-for="${CSS.escape(removedKey)}"]`) : []),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    // Count only real cart line rows — companion offer cards in rowsToRemove
    // aren't cart lines and would skew the comparison.
    const cartLineRowsToRemove = rowsToRemove.filter(
      (row) => row instanceof HTMLElement && row.dataset.line !== undefined
    );
    const isEmptyCart = cartLineRowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {Record<string, number>} [config.updates] - Line-item-key keyed
   *   quantities; when present the request goes to /cart/update.js so several
   *   lines (unit + companion subscription) change in one atomic call.
   * @param {boolean} [config.clampCompanions] - Multi-line updates only:
   *   after the write, clamp companion subscriptions that a unit cap left
   *   outnumbering their unit. Default true; false on the clamp's own write so
   *   a cap that also refuses the clamp can't loop.
   * @returns {Promise<void>} Settles when the request + morph have finished.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity, updates, clampCompanions = true } = config;
    const { cartTotal } = this.refs;
    const shouldClampCompanions = Boolean(updates) && clampCompanions;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify(
      updates
        ? {
            updates,
            sections: Array.from(sectionsToUpdate).join(','),
            sections_url: window.location.pathname,
          }
        : {
            line: line,
            quantity: quantity,
            sections: Array.from(sectionsToUpdate).join(','),
            sections_url: window.location.pathname,
          }
    );

    cartTotal?.shimmer();

    return this.#fetchCart(
      updates ? `${Theme.routes.cart_update_url}` : `${Theme.routes.cart_change_url}`,
      fetchConfig('json', { body })
    )
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          // onLineItemRemove applies its row removal (or swaps in the empty-cart
          // template) OPTIMISTICALLY, before this response lands. If the row is
          // gone, #handleCartError has nowhere to write and returns false — then
          // the drawer is showing a cart emptier than the server's, and checkout
          // would bill the lines it still holds. Re-render server truth.
          const surfaced = this.#handleCartError(line, parsedResponseText);
          // A cap error on the unit (quantity rule / inventory) says nothing
          // about what Shopify did to the companion line in the same request —
          // re-read and clamp. That re-renders, so the plain re-render is only
          // the fallback when there was nothing to clamp.
          if (shouldClampCompanions) {
            return this.#clampCompanionsFromServer().then((clamped) => {
              if (!clamped && !surfaced) return this.#reRender();
            });
          }
          if (!surfaced) this.#reRender();
          return;
        }

        // Status payloads (e.g. stale line key from a concurrent mutation)
        // carry no sections — re-render server truth instead of crashing
        // into .catch and leaving stale UI (same self-heal as #addCompanionLine).
        if (parsedResponseText.status || !parsedResponseText.sections?.[this.sectionId]) {
          console.error(parsedResponseText.description || parsedResponseText.message || 'Cart update failed');
          this.#reRender();
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], { mode: this.isDrawer ? 'hydration' : 'full' });

        this.#updateCartQuantitySelectorButtonStates();

        // Backstop: if anything left a subscription above its unit, clamp it
        // now. The response carries the cart, so checking costs no extra
        // request.
        if (shouldClampCompanions && Array.isArray(parsedResponseText.items)) {
          const overage = companionOverageUpdates(parsedResponseText.items);
          if (Object.keys(overage).length > 0) {
            return this.updateQuantity({ line, quantity, action: config.action, updates: overage, clampCompanions: false });
          }
        }
      })
      .catch((error) => {
        console.error(error);
        // Network/parse failure after a possible server-side change (and any
        // optimistic UI already applied, e.g. a disabled companion checkbox
        // or removed rows) — re-render server truth instead of leaving the
        // drawer stale (same self-heal as #addCompanionLine).
        resetShimmer(this);
        this.#reRender();
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string|Record<string, string[]>} parsedResponseText.errors - The errors.
   * @returns {boolean} True if the error was surfaced (or self-healed). False
   *   means the target row is gone — the caller must re-render, because any
   *   optimistic UI it applied is now stranded against a cart that still holds
   *   the line.
   */
  #handleCartError = (line, parsedResponseText) => {
    // Row-scoped lookup, never positional: rows render reversed and companion
    // rows consume line numbers without registering a quantitySelectors[] ref,
    // so quantitySelectors[line - 1] can never reliably align.
    const row = this.#rowForLine(line);

    // Companion subscription lines have no quantity input or error refs, and
    // the uncheck toggle disabled its checkbox pending a morph this error
    // path never runs — re-render server truth so the toggle comes back
    // alive in a state consistent with the cart (same self-heal as
    // #addCompanionLine's error path).
    if (row?.dataset.companionParentKey) {
      console.error(parsedResponseText.errors);
      this.#reRender();
      return true;
    }

    const quantityInput = row?.querySelector('input[data-cart-line]');

    if (quantityInput instanceof HTMLInputElement) {
      // No morph ran on the error path, so defaultValue is still the
      // server-rendered pre-change quantity.
      quantityInput.value = quantityInput.defaultValue;
    }

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement) || !(cartItemErrorContainer instanceof HTMLElement)) {
      console.error(parsedResponseText.errors);
      return false;
    }

    // /cart/change.js returns `errors` as a string; /cart/update.js (the
    // companion mirror and removal cascade) returns it KEYED, e.g.
    // {"errors":{"updates":["…"]}} — assigning that object rendered the literal
    // text "[object Object]" in the row's alert box.
    const { errors } = parsedResponseText;
    cartItemError.textContent =
      typeof errors === 'string' ? errors : Object.values(errors ?? {}).flat().join(' ');
    cartItemErrorContainer.classList.remove('hidden');
    return true;
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      this.#reRender();
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      this.#reRender();
    }
  };

  /**
   * Re-reads the cart and clamps any companion subscription line that
   * outnumbers its unit (see companionOverageUpdates). Keys come from the
   * fresh read, never the DOM — a stale key resolves onto the newest line of
   * its variant (see #updateUnitWithCompanions).
   * @returns {Promise<boolean>} True when a clamp was written (and morphed).
   */
  async #clampCompanionsFromServer() {
    // Hold the cart across the read so a stepper click can't interleave.
    this.#disableCartItems();
    try {
      /** @type {Record<string, number>} */
      let updates = {};
      try {
        const cart = await fetchCartState({ fresh: true });
        if (Array.isArray(cart?.items)) updates = companionOverageUpdates(cart.items);
      } catch (error) {
        console.error('[cart] could not read the cart to clamp companion quantities', error);
        return false;
      }
      if (Object.keys(updates).length === 0) return false;

      await this.updateQuantity({ line: 0, quantity: 0, action: 'change', updates, clampCompanions: false });
      return true;
    } finally {
      this.#enableCartItems();
    }
  }

  /**
   * Disables the cart items. Counted: overlapping mutations (keyboard input
   * is not blocked by pointer-events, so a blur-commit can start while
   * another request is in flight) each take the lock, and only the LAST
   * release re-enables and resolves it — otherwise the first mutation to
   * finish would unfreeze the UI and let onCartFormSubmit navigate while a
   * second request is still on the wire.
   */
  /**
   * True while ANY cart-items-component has a debounced quantity change
   * waiting or a mutation in flight. Checkout must never navigate on a stale
   * cart, and the work is not necessarily owned by the instance that owns the
   * clicked checkout button.
   * @returns {boolean}
   */
  static #anyWorkPending() {
    for (const instance of cartItemsInstances) {
      if (instance.#hasPendingLineChange() || instance.#cartLock) return true;
    }
    return false;
  }

  /**
   * This component's checkout submitter.
   * @returns {HTMLElement | null}
   */
  #checkoutButton() {
    const button = this.querySelector('button[name="checkout"]');
    return button instanceof HTMLElement ? button : null;
  }

  /**
   * Marks the checkout button busy while a submit is being held for pending
   * cart work. `aria-busy` (not `disabled`) on purpose: a disabled submitter
   * that a bug fails to re-enable would kill checkout outright, and the
   * functional guarantee is onCartFormSubmit's wait, not the attribute.
   * @param {boolean} busy
   */
  #setCheckoutBusy(busy) {
    for (const instance of cartItemsInstances) {
      const button = instance.#checkoutButton();
      if (!button) continue;
      if (busy) {
        button.setAttribute('aria-busy', 'true');
      } else {
        button.removeAttribute('aria-busy');
      }
    }
  }

  #disableCartItems() {
    this.#cartLockDepth += 1;
    this.classList.add('cart-items-disabled');
    if (!this.#cartLock) {
      /** @type {() => void} */
      let release = () => {};
      const promise = new Promise((resolve) => {
        release = () => resolve(undefined);
      });
      this.#cartLock = { promise, release };
    }
    this.#setCheckoutBusy(true);
  }

  /**
   * Enables the cart items once every holder has released.
   */
  #enableCartItems() {
    this.#cartLockDepth = Math.max(0, this.#cartLockDepth - 1);
    if (this.#cartLockDepth > 0) return;

    this.classList.remove('cart-items-disabled');
    this.#cartLock?.release();
    this.#cartLock = null;
    // Another instance may still be mid-mutation — busy is a document-wide
    // condition, not a per-component one.
    this.#setCheckoutBusy(CartItemsComponent.#anyWorkPending());
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
